<!-- routes/Referral.svelte -->
<script>
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import Header from "lib/components/Header.svelte";
  import Footer from "lib/components/Footer.svelte";
  import Container from "lib/components/Container.svelte";
  import Button from "lib/components/Button.svelte";
  import Hero from "lib/components/Hero.svelte";
  import { trackEvent } from "utils/analytics.js";
  import {
    APP_URL,
    TERMS_AND_CONDITIONS,
    PRIVACY_POLICY,
    AML_POLICY,
    COOKIE_POLICY,
  } from "utils/menu.js";
  import { apiPostJSON } from "utils/api";

  // Hero customization for referral page
  let heroConfig = { ctaText: "", ctaLink: "#referral-form" };

  // Form state
  let referralCode = "";
  let isSubmitting = false;
  let isCodeValid = false;
  let isCodeChecked = false;

  // Get referral code from URL path
  const path = window.location?.pathname;
  if (path && path.includes("/")) {
    const codeFromPath = path.split("/").pop();
    if (codeFromPath && codeFromPath !== "referral") {
      referralCode = codeFromPath;
    }
  }

  function getAppUrl(referral) {
    return `${APP_URL}?signup=true${referral ? `&referral=${referral}` : ""}`;
  }

  function resetForm() {
    referralCode = "";
    isCodeChecked = false;
    isCodeValid = false;
  }

  async function checkReferralCode() {
    if (!referralCode.trim()) {
      isCodeValid = false;
      isCodeChecked = true;
      return;
    }

    try {
      const response = await apiPostJSON("/user/referral/verify/", {
        referral: referralCode.trim(),
      });
      isCodeValid = !!response.valid;
    } catch (e) {
      console.log("Referral error: ", e);
      isCodeValid = false;
    }
    isCodeChecked = true;
  }

  async function handleSubmit() {
    if (isSubmitting) return;

    isSubmitting = true;

    await checkReferralCode();

    if (isCodeValid) {
      try {
        const url = getAppUrl(referralCode);

        // Track registration attempt
        trackEvent("LoginRedirect", {
          referralCode: referralCode ?? "",
          source: "referral_page",
        });

        resetForm();
        window.location = url;
      } catch (error) {
        console.error("Registration redirect failed:", error);
        isSubmitting = false;
      }
    } else {
      isSubmitting = false;
    }
  }

  async function handleReferralCodeInput() {
    isCodeChecked = false;
    isCodeValid = false;
  }

  onMount(() => {
    trackEvent("PageView", {
      page: "Referral",
      url: window?.location?.href,
    });

    // Auto-check referral code if it was provided in URL
    if (referralCode) {
      checkReferralCode();
    }

    // Set hero config with reactive text
    heroConfig.ctaText = $_("register.button");
  });
</script>

<svelte:head>
  <title>Referral - Join Liquid Noble</title>
  <meta
    name="description"
    content="You've been invited to join Liquid Noble. Register with your referral code to access securely purchase, store, and transfer physical precious metals."
  />
</svelte:head>

<Header />

<!-- Hero Section with Info Cards -->
<Hero ctaText={heroConfig.ctaText} ctaLink={heroConfig.ctaLink} />

<!-- Registration Form Section -->
<Container
  variant="dark"
  size="large"
  class="referral-container"
  id="referral-form"
>
  <div class="referral-content">
    <div class="referral-text">
      <h2 class="referral-title">{$_("referral.title")}</h2>
      <p class="referral-subtitle">
        {$_("referral.subtitle")}
      </p>

      <div class="features-list">
        <div class="feature-item">
          <div class="feature-icon">🛡️</div>
          <span>{$_("referral.feature1")}</span>
        </div>
        <div class="feature-item">
          <div class="feature-icon">⚡</div>
          <span>{$_("referral.feature2")}</span>
        </div>
        <div class="feature-item">
          <div class="feature-icon">🎯</div>
          <span>{$_("referral.feature3")}</span>
        </div>
      </div>

      <form on:submit|preventDefault={handleSubmit} class="referral-form">
        <div class="form-section">
          <p class="referral-text-instruction">
            {$_("referral.instruction")}
          </p>

          <div class="form-row">
            <div class="form-group">
              <input
                type="text"
                placeholder={$_("referral.placeholder")}
                maxlength="64"
                bind:value={referralCode}
                on:input={handleReferralCodeInput}
                class="referral-input {isCodeChecked &&
                  (isCodeValid ? 'valid' : 'invalid')}"
                required
              />
              {#if isCodeChecked && !isCodeValid}
                <span class="validation-text">
                  {$_("referral.validation.invalid")}
                </span>
              {/if}
            </div>

            <Button
              variant="primary"
              size="compact"
              type="submit"
              disabled={isSubmitting || (isCodeChecked && !isCodeValid)}
              class="referral-button"
            >
              {isSubmitting
                ? $_("referral.buttonSubmitting")
                : $_("referral.button")}
            </Button>
          </div>
        </div>
      </form>

      <div class="legal-notice">
        <p>
          {@html $_("referral.legalNotice", {
            values: {
              terms: `<a href="${TERMS_AND_CONDITIONS}" target="_blank" rel="noopener noreferrer">${$_("register.legalTerms")}</a>`,
              privacy: `<a href="${PRIVACY_POLICY}" target="_blank" rel="noopener noreferrer">${$_("register.legalPrivacy")}</a>`,
              aml: `<a href="${AML_POLICY}" target="_blank" rel="noopener noreferrer">${$_("register.legalAml")}</a>`,
              cookie: `<a href="${COOKIE_POLICY}" target="_blank" rel="noopener noreferrer">${$_("register.legalCookie")}</a>`,
            },
          })}
        </p>
      </div>
    </div>

    <div class="referral-visual">
      <div class="visual-container">
        <div class="gold-bars">
          <div class="gold-bar gold-bar-1">
            <div class="gold-shine"></div>
          </div>
          <div class="gold-bar gold-bar-2">
            <div class="gold-shine"></div>
          </div>
          <div class="gold-bar gold-bar-3">
            <div class="gold-shine"></div>
          </div>
        </div>

        <div class="stats-card">
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat1.value")}</div>
            <div class="stat-label">{$_("register.stat1.label")}</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat2.value")}</div>
            <div class="stat-label">{$_("register.stat2.label")}</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat3.value")}</div>
            <div class="stat-label">{$_("register.stat3.label")}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</Container>

<Footer />

<style lang="scss">
  @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap");

  :global(body) {
    background-color: #1a091c;
    margin: 0;
    padding: 0;
    font-family: "Inter", sans-serif;
    min-height: 100vh;
  }

  /* Registration Section */
  .referral-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;
    margin: 1rem 0;
  }

  .referral-text {
    max-width: 600px;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
  }

  .referral-title {
    font-size: 2.8rem;
    font-weight: 800;
    color: var(--text-color1);
    margin-bottom: 1rem;
    line-height: 1.1;
  }

  .referral-subtitle {
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--text-color2);
    margin-bottom: 1.8rem;
  }

  .features-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-bottom: 2rem;
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: var(--text-color2);
    font-size: 0.95rem;
  }

  .feature-icon {
    width: 36px;
    height: 36px;
    background: rgba(210, 165, 82, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  .referral-form {
    margin-bottom: 1.5rem;
  }

  .form-section {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 1.5rem;
    text-align: center;
  }

  .referral-text-instruction {
    color: var(--text-color2);
    font-size: 0.9rem;
    margin-bottom: 1.2rem;
    line-height: 1.5;
  }

  .form-row {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    justify-content: center;
  }

  .form-group {
    flex: 1;
    max-width: 250px;
    position: relative;
  }

  .referral-input {
    width: 100%;
    padding: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-color2);
    font-size: 1rem;
    transition: all 0.3s ease;
    box-sizing: border-box;
    height: 48px;

    &:focus {
      outline: none;
      border-color: var(--text-color1);
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 2px rgba(210, 165, 82, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    &.valid {
      border-color: #28a745;
      box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
    }

    &.invalid {
      border-color: #dc3545;
      box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
    }
  }

  .validation-text {
    color: #dc3545;
    font-size: 0.8rem;
    margin-top: 0.5rem;
    display: block;
    text-align: left;
  }

  :global(.referral-button) {
    white-space: nowrap;
    min-width: 180px;
  }

  .legal-notice {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    margin-top: 0.5rem;

    a {
      color: var(--text-color1);
      text-decoration: none;
      transition: color 0.3s ease;
      font-size: 0.85rem;

      &:hover {
        color: #fff;
        text-decoration: underline;
      }
    }
  }

  .referral-visual {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .visual-container {
    position: relative;
    width: 100%;
    max-width: 350px;
    height: 450px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .gold-bars {
    position: relative;
    width: 100%;
    height: 250px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    transform: perspective(1000px) rotateY(-20deg) rotateX(10deg);
  }

  .gold-bar {
    width: 70px;
    height: 150px;
    background: linear-gradient(
      135deg,
      #f4d03f 0%,
      #f1c40f 20%,
      #e6ac00 40%,
      #d4af37 60%,
      #c9a328 80%,
      #b8941f 100%
    );
    border-radius: 6px;
    position: relative;
    box-shadow:
      0 15px 40px rgba(0, 0, 0, 0.4),
      0 5px 15px rgba(255, 215, 0, 0.3),
      inset 0 2px 10px rgba(255, 255, 255, 0.3),
      inset 0 -2px 10px rgba(0, 0, 0, 0.2);
    animation: float 6s ease-in-out infinite;
    overflow: hidden;

    &::before {
      content: "GOLD";
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      width: 55px;
      height: 28px;
      background: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.4) 0%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.3) 100%
      );
      border-radius: 5px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.6rem;
      font-weight: bold;
      color: rgba(0, 0, 0, 0.6);
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
      letter-spacing: 0.5px;
    }

    &::after {
      content: "999.9";
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      color: rgba(0, 0, 0, 0.6);
      font-size: 0.7rem;
      font-weight: bold;
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
    }
  }

  .gold-bar-1 {
    animation-delay: 0s;
    z-index: 3;
  }

  .gold-bar-2 {
    animation-delay: 1s;
    z-index: 2;
  }

  .gold-bar-3 {
    animation-delay: 2s;
    z-index: 1;
  }

  .gold-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    animation: shine 3s ease-in-out infinite;
  }

  .stats-card {
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 1rem 1.2rem;
    display: flex;
    gap: 1.2rem;
    animation: slideUp 1s ease-out 0.5s both;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }

  .stat-item {
    text-align: center;
    min-width: 50px;
  }

  .stat-value {
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--text-color1);
    line-height: 1;
    margin-bottom: 0.2rem;
  }

  .stat-label {
    font-size: 0.75rem;
    color: var(--text-color2);
    font-weight: 500;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) rotateY(2deg);
    }
    50% {
      transform: translateY(8px) rotateY(-2deg);
    }
  }

  @keyframes shine {
    0% {
      left: -100%;
    }
    50% {
      left: 100%;
    }
    100% {
      left: 100%;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .referral-content {
      grid-template-columns: 1fr;
      gap: 2rem;
      text-align: center;
    }

    .referral-text {
      max-width: none;
    }

    .visual-container {
      max-width: 300px;
      height: 320px;
    }

    .gold-bars {
      height: 200px;
    }

    .gold-bar {
      width: 60px;
      height: 120px;
    }
  }

  @media (max-width: 768px) {
    .referral-title {
      font-size: 2.2rem;
    }

    .referral-subtitle {
      font-size: 1rem;
    }

    .form-row {
      flex-direction: column;
      gap: 1rem;
    }

    .form-group {
      max-width: none;
    }

    :global(.referral-button) {
      width: 100%;
      min-width: auto;
    }

    .referral-content {
      gap: 1.5rem;
    }

    .visual-container {
      max-width: 280px;
      height: 290px;
    }

    .gold-bars {
      height: 180px;
      gap: 0.8rem;
    }

    .gold-bar {
      width: 55px;
      height: 110px;
    }

    .stats-card {
      padding: 0.8rem;
      gap: 0.8rem;
    }

    .legal-notice {
      font-size: 0.8rem;
    }

    .legal-notice a {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 480px) {
    .referral-title {
      font-size: 1.8rem;
    }

    .referral-subtitle {
      font-size: 0.95rem;
    }

    .form-section {
      padding: 1.2rem;
    }

    .feature-item {
      font-size: 0.85rem;
    }

    .feature-icon {
      width: 32px;
      height: 32px;
      font-size: 1rem;
    }
  }
</style>
